/**
 * CUST-EXP-001A1 · Data-truth helpers for the Customer Command Centre.
 *
 * These helpers deliberately return honest neutral fallbacks when
 * canonical data does not positively support a forward-looking claim.
 * They do NOT invent numbers, dates or reassurance.
 */

/** Human-friendly local date (no time) — mirrors /appointments. */
export function formatLocalDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "long", day: "numeric", month: "long",
        });
    } catch { return ""; }
}

/** Human-friendly local time only. Used when no arrival window exists. */
export function formatLocalTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
}

/** Present the appointment's time band:
 *   • prefer the installer-supplied arrival window (a decision made
 *     on /appointments) — the raw scheduled_at time is often a
 *     placeholder created-at timestamp;
 *   • otherwise the local scheduled time;
 *   • otherwise nothing.
 */
export function appointmentTimeLabel(appt) {
    if (!appt) return "";
    if (appt.arrival_window) return appt.arrival_window;
    return formatLocalTime(appt.scheduled_at);
}

/** Decide whether an estimated-completion string is safe to display
 *  as a forward-looking value. Rules:
 *   • parses as an ISO/date string that lies strictly in the future → display verbatim;
 *   • plain-year token where the year is > today's year → display verbatim;
 *   • all other cases (past year, past date, empty, "TBD") → return null.
 */
export function truthfulEstimate(text) {
    if (!text) return null;
    const s = String(text).trim();
    if (!s) return null;
    const now = new Date();
    // Try direct ISO / date parse.
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.getTime() > now.getTime() ? s : null;
    }
    // Extract year token — treat as future only if strictly ahead.
    const y = s.match(/(20\d{2})/);
    if (y) {
        const year = Number(y[1]);
        if (year > now.getFullYear()) return s;
        if (year === now.getFullYear() && /q4|late|winter|dec/i.test(s) && now.getMonth() < 9) return s;
        return null;
    }
    // Explicit neutral labels used by ops are fine — never fabricated.
    if (/^(being reviewed|tbc|tbd|under review)$/i.test(s)) return s;
    return null;
}

/** Return the reassurance headline for the current project. Positive
 *  wording is only permitted when canonical data supports it:
 *    • lifecycle === "commissioned" → confident success;
 *    • state === "on_hold"          → warning;
 *    • otherwise                    → neutral progress-anchored copy
 *                                     that never claims "on track".
 */
export function statusHeadline(project) {
    if (!project) return { line: "Getting set up.", tone: "neutral" };
    const p = project.progress ?? 0;
    const state = (project.state || "").toLowerCase();
    if (state === "on_hold") return { line: "Your project is paused while we sort a few things out.", tone: "warning" };
    if (p >= 100) return { line: "Your system is live and everything is in place.", tone: "success" };
    if (p >= 75) return { line: `Installation is nearly complete — ${p}% done.`, tone: "neutral" };
    if (p >= 40) return { line: `Installation is progressing — ${p}% done.`, tone: "neutral" };
    if (p >  0)  return { line: `Early stages under way — ${p}% done.`, tone: "neutral" };
    return { line: "Your installation is about to begin.", tone: "neutral" };
}
